import { useState } from "react";
import { toast } from "sonner";
import { Check, Loader2, Mail, Printer, Send } from "lucide-react";
import { submitFlagstarEngagement } from "@/lib/api";

const TEAL = "#00586B";
const GOLD = "#FFC425";
const CHARCOAL = "#1A1A1A";

const SETUP_FEE = 8000;
const SETUP_PROMO = 6400;
const PER_CRITICAL_VENDOR = 600;
const QUARTERLY_REVIEW = 1200;
const PLATFORM_MONTHLY = 1000;
const FRACTIONAL_RATE = 200;

const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

export function FlagstarEngagement({ engagementPct, sectionsViewed, onPrint }) {
  const [termSheet, setTermSheet] = useState(true);
  const [vendorsEnabled, setVendorsEnabled] = useState(true);
  const [criticalVendors, setCriticalVendors] = useState(100);
  const [quarterlyReviews, setQuarterlyReviews] = useState(true);
  const [fractionalHours, setFractionalHours] = useState(0);
  const [form, setForm] = useState({ open: false, name: "", email: "", note: "", sending: false, sent: false });

  const setupFee = termSheet ? SETUP_PROMO : SETUP_FEE;
  const monthEnd = new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
    .toLocaleDateString("en-US", { month: "long", day: "numeric" });
  const vendorOnboarding = vendorsEnabled ? criticalVendors * PER_CRITICAL_VENDOR : 0;
  const oneTimeTotal = setupFee + vendorOnboarding;
  const quarterlyTotal = vendorsEnabled && quarterlyReviews ? criticalVendors * QUARTERLY_REVIEW : 0;
  const fractionalMonthly = fractionalHours * FRACTIONAL_RATE;
  const monthlyTotal = PLATFORM_MONTHLY + fractionalMonthly;

  const sendToColin = async () => {
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Name and email are required.");
      return;
    }
    setForm((f) => ({ ...f, sending: true }));
    try {
      const res = await submitFlagstarEngagement({
        name: form.name.trim(),
        email: form.email.trim(),
        note: form.note.trim() || null,
        setup_fee: setupFee,
        term_sheet_promo: termSheet,
        critical_vendors_enabled: vendorsEnabled,
        critical_vendors: vendorsEnabled ? criticalVendors : 0,
        vendor_onboarding_total: vendorOnboarding,
        platform_monthly: PLATFORM_MONTHLY,
        quarterly_reviews_enabled: quarterlyReviews,
        quarterly_review_total: quarterlyTotal,
        fractional_hours: fractionalHours,
        fractional_monthly: fractionalMonthly,
        monthly_total: monthlyTotal,
        one_time_total: oneTimeTotal,
        engagement_pct: engagementPct,
        sections_viewed: sectionsViewed,
      });
      if (res?.ok) {
        setForm((f) => ({ ...f, sending: false, sent: true }));
        toast.success("Sent — Colin will follow up to schedule the discovery call.");
      } else {
        setForm((f) => ({ ...f, sending: false }));
        toast.error("Something went wrong — please try again.");
      }
    } catch {
      setForm((f) => ({ ...f, sending: false }));
      toast.error("Network error — email colindarke@complisun.com directly.");
    }
  };

  const inputCls = "w-full rounded-lg px-3.5 py-2.5 text-[14px] bg-white/10 text-white placeholder:text-white/40 border border-white/25 focus:outline-none focus:border-white/60";

  return (
    <section id="pricing" className="fs-anchor py-14 md:py-20">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="fs-eyebrow">Pricing · design your engagement</div>
        <h2 className="fs-h text-4xl md:text-5xl mt-3 max-w-3xl">
          Size it to your vendor book. <span style={{ color: TEAL }}>See the number live.</span>
        </h2>
        <p className="mt-4 text-lg max-w-3xl leading-relaxed" style={{ color: CHARCOAL, opacity: 0.72 }}>
          Set how many critical vendors get a dedicated environment, add fractional expert hours if you
          want them — then download the PDF or send the configuration straight to Colin.
        </p>

        <div className="mt-9 grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-5 items-start">
          <div className="space-y-4">
            <div className="fs-card p-6">
              <div className="fs-eyebrow mb-4">The base</div>
              <div className="flex flex-wrap items-center justify-between gap-3 pb-4 border-b border-black/8">
                <div>
                  <div className="fs-h text-base">Set-up / implementation</div>
                  <div className="text-[12px] opacity-60">One-time — CompliSun configured to Flagstar's actual workflows</div>
                </div>
                <div className="text-right">
                  <div className="fs-h text-xl" data-testid="fs-setup-fee">
                    {termSheet && <span className="line-through mr-2 text-sm opacity-40">{fmt(SETUP_FEE)}</span>}
                    {fmt(setupFee)}<span className="text-sm opacity-50"> one-time</span>
                  </div>
                  <label className="flex items-center gap-1.5 text-[12px] font-bold cursor-pointer justify-end" style={{ color: TEAL }}>
                    <input type="checkbox" checked={termSheet} onChange={(e) => setTermSheet(e.target.checked)}
                           data-testid="fs-termsheet-toggle" className="accent-current" />
                    20% off — term sheet signed by {monthEnd}
                  </label>
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-between gap-3 pt-4">
                <div>
                  <div className="fs-h text-base">Platform fee — all other vendors</div>
                  <div className="text-[12px] opacity-60">Intake · tiering · reviews · continuous monitoring · leadership scorecard</div>
                </div>
                <div className="fs-h text-xl">{fmt(PLATFORM_MONTHLY)}<span className="text-sm opacity-50">/mo flat</span></div>
              </div>
            </div>

            <div className="fs-card p-6">
              <div className="fs-eyebrow mb-1">Critical vendors · dedicated environments</div>
              <p className="text-[13px] mb-3 opacity-65">
                Each critical vendor gets its own multi-tenant environment — documents, screenings, findings,
                and remediation walled off per vendor, all feeding your enterprise scorecard.
                <strong> Every vendor onboarded carries a one-time {fmt(PER_CRITICAL_VENDOR)} onboarding fee.</strong>
              </p>
              <button type="button" onClick={() => setVendorsEnabled((v) => !v)}
                      data-testid="fs-vendors-toggle"
                      className="flex items-center gap-3 w-full text-left mb-3">
                <span className="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors"
                      style={{ background: vendorsEnabled ? TEAL : "#00000022" }}>
                  <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform"
                        style={{ transform: vendorsEnabled ? "translateX(22px)" : "translateX(2px)" }} />
                </span>
                <span className="text-[13px] font-bold">
                  {vendorsEnabled ? "Included — dedicated environments per critical vendor" : "Not included — add anytime"}
                </span>
              </button>
              {vendorsEnabled && (
                <>
                  <input type="range" min={50} max={200} value={criticalVendors}
                         onChange={(e) => setCriticalVendors(Number(e.target.value))}
                         data-testid="fs-vendors-slider" className="w-full accent-[#00586B]" />
                  <div className="flex justify-between items-baseline mt-1">
                    <div className="text-[13px] font-bold">{criticalVendors} critical vendors</div>
                    <div className="fs-h text-lg" style={{ color: TEAL }} data-testid="fs-vendor-onboarding">{fmt(vendorOnboarding)} one-time</div>
                  </div>
                </>
              )}
            </div>

            <div className="fs-card p-6" style={vendorsEnabled ? undefined : { opacity: 0.55 }}>
              <div className="fs-eyebrow mb-1">Quarterly deep review · proposed — your call</div>
              <p className="text-[13px] mb-3 opacity-65">
                We propose a {fmt(QUARTERLY_REVIEW)} quarterly review for each critical vendor — a full re-screen,
                document refresh, and findings check every quarter instead of waiting for the annual cycle.
                Entirely optional; toggle it off if annual cadence works for you.
              </p>
              <button type="button" onClick={() => setQuarterlyReviews((v) => !v)}
                      disabled={!vendorsEnabled}
                      data-testid="fs-quarterly-toggle"
                      className="flex items-center gap-3 w-full text-left">
                <span className="relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors"
                      style={{ background: vendorsEnabled && quarterlyReviews ? TEAL : "#00000022" }}>
                  <span className="absolute top-0.5 h-5 w-5 rounded-full bg-white shadow transition-transform"
                        style={{ transform: vendorsEnabled && quarterlyReviews ? "translateX(22px)" : "translateX(2px)" }} />
                </span>
                <span className="flex-1 flex justify-between items-baseline">
                  <span className="text-[13px] font-bold">
                    {!vendorsEnabled
                      ? "Enable critical-vendor environments above to add quarterly reviews"
                      : quarterlyReviews
                        ? `Included — ${criticalVendors} critical vendors × ${fmt(QUARTERLY_REVIEW)}/quarter`
                        : "Not included — add anytime"}
                  </span>
                  <span className="fs-h text-lg" style={{ color: TEAL }} data-testid="fs-quarterly-total">
                    {quarterlyTotal > 0 ? `${fmt(quarterlyTotal)}/qtr` : "—"}
                  </span>
                </span>
              </button>
            </div>

            <div className="fs-card p-6">
              <div className="fs-eyebrow mb-1">Fractional add-on · compliance expert support</div>
              <p className="text-[13px] mb-3 opacity-65">
                As-needed hours from CompliSun's practitioner bench — exam prep, escalated reviews, remediation
                oversight. {fmt(FRACTIONAL_RATE)}/hr blended, no monthly minimum.
              </p>
              <input type="range" min={0} max={60} step={2} value={fractionalHours}
                     onChange={(e) => setFractionalHours(Number(e.target.value))}
                     data-testid="fs-hours-slider" className="w-full accent-[#00586B]" />
              <div className="flex justify-between items-baseline mt-1">
                <div className="text-[13px] font-bold">{fractionalHours} hrs/mo</div>
                <div className="fs-h text-lg" style={{ color: TEAL }} data-testid="fs-fractional-monthly">{fractionalHours > 0 ? `${fmt(fractionalMonthly)}/mo` : "none — add anytime"}</div>
              </div>
            </div>
          </div>

          {/* Summary */}
          <div className="rounded-2xl p-6 lg:sticky lg:top-24" style={{ background: CHARCOAL, color: "#fff" }} data-testid="fs-summary">
            <div className="text-[11px] font-extrabold uppercase tracking-[0.18em]" style={{ color: GOLD }}>Your engagement</div>
            <div className="mt-4 space-y-2.5 text-[14px]">
              <div className="flex justify-between"><span className="opacity-70">Set-up (one-time){termSheet ? " · 20% term-sheet discount" : ""}</span><strong data-testid="fs-summary-setup">{fmt(setupFee)}</strong></div>
              {vendorsEnabled && (
                <div className="flex justify-between"><span className="opacity-70">Vendor onboarding · {criticalVendors} × {fmt(PER_CRITICAL_VENDOR)}</span><strong data-testid="fs-summary-vendors">{fmt(vendorOnboarding)}</strong></div>
              )}
              <div className="flex justify-between"><span className="opacity-70">Platform (flat)</span><strong>{fmt(PLATFORM_MONTHLY)}/mo</strong></div>
              {quarterlyTotal > 0 && (
                <div className="flex justify-between"><span className="opacity-70">Quarterly reviews · {criticalVendors} × {fmt(QUARTERLY_REVIEW)}</span><strong data-testid="fs-summary-quarterly">{fmt(quarterlyTotal)}/qtr</strong></div>
              )}
              {fractionalHours > 0 && (
                <div className="flex justify-between"><span className="opacity-70">Fractional experts · {fractionalHours}h</span><strong>{fmt(fractionalMonthly)}/mo</strong></div>
              )}
            </div>
            <div className="mt-5 pt-4 border-t border-white/20">
              <div className="flex justify-between items-baseline">
                <span className="text-[11px] uppercase tracking-[0.14em] opacity-60">One-time total</span>
                <span className="fs-h text-2xl" style={{ color: "#fff" }} data-testid="fs-onetime-total">{fmt(oneTimeTotal)}</span>
              </div>
              {quarterlyTotal > 0 && (
                <div className="flex justify-between items-baseline mt-2">
                  <span className="text-[11px] uppercase tracking-[0.14em] opacity-60">Quarterly total (proposed)</span>
                  <span className="fs-h text-2xl" style={{ color: "#fff" }} data-testid="fs-quarterly-summary-total">{fmt(quarterlyTotal)}<span className="text-sm text-white/70">/qtr</span></span>
                </div>
              )}
              <div className="flex justify-between items-baseline mt-2">
                <span className="text-[11px] uppercase tracking-[0.14em] opacity-60">Monthly total</span>
                <span className="fs-h text-4xl" style={{ color: GOLD }} data-testid="fs-monthly-total">{fmt(monthlyTotal)}<span className="text-lg text-white/70">/mo</span></span>
              </div>
            </div>

            <div className="mt-6 space-y-2" data-print="hide">
              <button type="button" onClick={() => setForm((f) => ({ ...f, open: !f.open }))}
                      data-testid="fs-email-toggle"
                      className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg text-[14px] font-bold transition-transform hover:-translate-y-0.5"
                      style={{ background: GOLD, color: CHARCOAL }}>
                <Mail className="h-4 w-4" /> Email this to Colin to start
              </button>
              <button type="button" onClick={onPrint} data-testid="fs-pdf"
                      className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg text-[14px] font-bold border-2 border-white/30 text-white">
                <Printer className="h-4 w-4" /> Download as PDF
              </button>
            </div>

            {form.open && !form.sent && (
              <div className="mt-4 space-y-2.5" data-print="hide" data-testid="fs-email-form">
                <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                       placeholder="Your name" data-testid="fs-name" className={inputCls} />
                <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                       placeholder="you@flagstar.com" type="email" data-testid="fs-email" className={inputCls} />
                <textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                          placeholder="Anything Colin should know? (optional)" rows={2} data-testid="fs-note"
                          className={`${inputCls} resize-none`} />
                <button type="button" onClick={sendToColin} disabled={form.sending} data-testid="fs-send"
                        className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-lg text-[14px] font-bold border border-white/40 text-white disabled:opacity-50">
                  {form.sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                  {form.sending ? "Sending…" : "Send my engagement"}
                </button>
              </div>
            )}
            {form.sent && (
              <div className="mt-4 rounded-lg p-4 text-center border" style={{ background: "rgba(255,196,37,0.12)", borderColor: GOLD }} data-testid="fs-sent">
                <Check className="h-6 w-6 mx-auto mb-1" style={{ color: GOLD }} />
                <div className="fs-h text-base" style={{ color: "#fff" }}>Sent to Colin.</div>
                <div className="text-[12px] mt-1 opacity-70">He'll reply within one business day — or call him at (313) 617-4473.</div>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
